import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { QueryConfig } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

/**
 * Backend response shape from
 * `GET /v1/orgs/{orgId}/projects/{projectId}/jobs/{jobId}`. The endpoint is
 * scope-checked on `(orgId, targetProjectId)` so a 404 from this query
 * means either the job doesn't exist or it belongs to a different
 * project — callers should treat both as "no in-flight job".
 */
export interface CopyRoomItemsJobStatus {
  id: string;
  type: 'CopyRoomItems' | 'CopyRoomToFloor' | 'CloneItem';
  status: 'pending' | 'running' | 'completed' | 'failed';
  response?: {
    copied?: number;
    skipped?: number;
    newRoomId?: string;
    /** First few skip reasons, surfaced by the worker so the user can see "why" without CloudWatch. */
    skipReasons?: { sourceItemId: string; reason: string }[];
    /** MT1-1426 item-clone job: ids of the created clones (creation order) + count. */
    itemIds?: string[];
    count?: number;
    /** MT1-1428 item-clone job: generated number + copied name per clone, for the post-clone edit screen. */
    items?: { id: string; itemNumber: string; name: string }[];
  };
  error?: string;
  createdAt: number;
  updatedAt: number;
  startedAt?: number;
  completedAt?: number;
  failedAt?: number;
}

export const getJobStatus = async (
  orgId: string,
  projectId: string,
  jobId: string,
): Promise<CopyRoomItemsJobStatus> => {
  const { data } = await api.get<unknown, ApiResponse<CopyRoomItemsJobStatus>>(
    `/orgs/${orgId}/projects/${projectId}/jobs/${jobId}`,
  );
  return data;
};

export const getJobStatusQueryKey = (orgId?: string, projectId?: string, jobId?: string) => [
  'org',
  orgId,
  'project',
  projectId,
  'job',
  jobId,
];

export const getJobStatusQueryOptions = (orgId?: string, projectId?: string, jobId?: string) =>
  queryOptions({
    queryKey: getJobStatusQueryKey(orgId, projectId, jobId),
    queryFn: () =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      getJobStatus(orgId!, projectId!, jobId!),
    enabled: !!orgId && !!projectId && !!jobId,
  });

interface UseJobStatusOptions {
  jobId: string | null | undefined;
  /**
   * Polling cadence in ms while the job is still pending/running. Stops once
   * the job reaches a terminal status. Defaults to 2s — fast enough to feel
   * responsive in the dialog without flooding the API for long-running jobs.
   */
  pollIntervalMs?: number;
  queryConfig?: QueryConfig<typeof getJobStatusQueryOptions>;
}

/**
 * Polls `GET /jobs/{jobId}` while the job is in flight, stops on completion or
 * failure. Returns the standard react-query result so callers can render
 * `isLoading` / `data.status` / `data.response` directly.
 */
export const useJobStatus = ({
  jobId,
  pollIntervalMs = 2000,
  queryConfig,
}: UseJobStatusOptions) => {
  const { orgId, projectId } = useScopeIds('org', 'project');
  return useQuery({
    ...getJobStatusQueryOptions(orgId, projectId, jobId ?? undefined),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling once the worker reaches a terminal state. Returning false
      // suspends the interval without unmounting the query.
      if (status === 'completed' || status === 'failed') return false;
      return pollIntervalMs;
    },
    ...queryConfig,
  });
};
